import { useTicketTitle } from "@/lib/hooks";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Building2, Wrench, TrendingUp, Wallet, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { PageHeader, StatusBadge, Money } from "@/components/common";

const STATUSES = ["ouvert", "attribue", "en_cours", "resolu", "cloture"];
const BAR = { ouvert: "#D97706", attribue: "#4F46E5", en_cours: "#2563EB", resolu: "#059669", cloture: "#94A3B8" };

function Kpi({ icon: Icon, label, value, sub, tone = "", testId, delay }) {
  return (
    <div className={`rise ${delay} card-lift rounded-sm border border-slate-200 bg-white p-5`} data-testid={testId}>
      <div className="flex items-center justify-between text-slate-500">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">{label}</span><Icon size={16} strokeWidth={1.6} />
      </div>
      <div className={`mt-3 font-data text-2xl font-medium text-slate-900 ${tone}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function StatusBars({ data, total }) {
  const { t } = usePrefs();
  return (
    <div className="space-y-3" data-testid="tickets-by-status">
      {STATUSES.map((s) => (
        <Link to={`/tickets?status=${s}`} key={s} className="group grid grid-cols-[80px_minmax(0,1fr)_28px] items-center gap-3 text-sm" data-testid={`status-bar-${s}`}>
          <span className="text-slate-600 group-hover:text-slate-900">{t(s)}</span>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${total ? (100 * data[s]) / total : 0}%`, background: BAR[s] }} /></div>
          <span className="text-right font-data text-xs">{data[s]}</span>
        </Link>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const title = useTicketTitle();
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const { data: d } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard").then((r) => r.data), refetchInterval: 15000 });
  const global = ["admin", "supervision"].includes(user.role);
  if (!d) return <div className="h-64 animate-pulse rounded-sm bg-slate-200/60" />;
  const showMoney = user.role !== "prestataire";
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={`${t("welcome")}, ${user.name}`} title={global ? t("supervision") : t("dashboard")} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {user.role !== "prestataire" && <Kpi icon={Building2} label={t("occupancy")} value={`${d.occupancy.rate}%`} sub={`${d.occupancy.occupied} / ${d.occupancy.total} ${t("units").toLowerCase()}`} testId="kpi-occupancy" delay="rise-1" />}
        {showMoney && <Kpi icon={Wallet} label={t("collected")} value={<Money amount={d.rent_eur.collected} currency="EUR" showContract={false} />} testId="kpi-collected" delay="rise-2" />}
        {showMoney && <Kpi icon={TrendingUp} label={t("unpaid")} value={<Money amount={d.rent_eur.unpaid} currency="EUR" showContract={false} />} tone={d.rent_eur.unpaid > 0 ? "!text-rose-700" : ""} sub={<>{t("pendingRent")} : <Money amount={d.rent_eur.pending} currency="EUR" showContract={false} /></>} testId="kpi-unpaid" delay="rise-3" />}
        <Kpi icon={Wrench} label={t("activeTickets")} value={d.active_tickets} sub={`${d.urgent_count} ${t("urgent").toLowerCase()} · ${d.overdue_count} ${t("overdue").toLowerCase()}`} testId="kpi-tickets" delay="rise-4" />
        {global && <Link to="/admin"><Kpi icon={UserCheck} label={t("accountsPending")} value={d.accounts_pending} tone={d.accounts_pending ? "!text-amber-700" : ""} testId="kpi-accounts-pending" delay="rise-4" /></Link>}
        {global && <Kpi icon={Clock} label={t("overdue")} value={d.overdue_count} tone={d.overdue_count ? "!text-amber-700" : ""} testId="kpi-overdue" delay="rise-4" />}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr_1.2fr]">
        <section className="rise rise-2 min-w-0 rounded-sm border border-slate-200 bg-white p-6">
          <h3 className="mb-5 font-display text-lg font-bold">{t("ticketsByStatus")}</h3>
          <StatusBars data={d.tickets_by_status} total={d.tickets_total} />
        </section>
        <section className="rise rise-3 min-w-0 rounded-sm border border-slate-200 bg-white p-6" data-testid="urgent-tickets">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold"><AlertTriangle size={16} className="text-rose-600" />{t("urgentTickets")}</h3>
          {d.urgent.length === 0 && <p className="text-sm text-slate-500">{t("noData")}</p>}
          <div className="divide-y divide-slate-100">
            {d.urgent.map((u) => (
              <Link key={u.id} to={`/tickets/${u.id}`} className="flex items-center justify-between gap-2 py-2.5 text-sm hover:text-[#996515]">
                <span className="min-w-0 truncate"><span className="mr-2 font-data text-xs text-slate-400">{u.number}</span>{title(u)}</span>
                <StatusBadge value={u.urgency} />
              </Link>
            ))}
          </div>
        </section>
        <section className="rise rise-4 min-w-0 rounded-sm border border-slate-200 bg-white p-6" data-testid="recent-activity">
          <h3 className="mb-4 font-display text-lg font-bold">{t("recentActivity")}</h3>
          <ol className="relative space-y-4 border-l border-slate-200 pl-5">
            {d.activity.map((a) => (
              <li key={a.id} className="relative text-sm">
                <span className="absolute -left-[25px] top-1.5 h-2 w-2 rounded-full bg-[#B8860B]" />
                {a.link ? <Link to={a.link} className="hover:text-[#996515]">{a.text}</Link> : a.text}
                <div className="mt-0.5 text-[11px] text-slate-400">{a.user_name} · {t(`role_${a.role}`)} · {fdatetime(a.created_at)}</div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}
